"use client";
import { useState, FormEvent } from "react";
import Link from "next/link";
import { Mail, ArrowLeft, Send, CheckCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  function validate() {
    if (!email) { setEmailError("Email address is required."); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailError("Enter a valid email address."); return false; }
    setEmailError("");
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json();
        setEmailError(data.error ?? "Something went wrong. Please try again.");
        return;
      }
      setSubmitted(true);
    } catch {
      setEmailError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="flex-1 flex flex-col items-center justify-center px-4 py-12"
      style={{ background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 40%, #bfdbfe 100%)" }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <Link href="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center font-bold text-sm text-white shadow-lg group-hover:bg-blue-700 transition-colors">
              M
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ color: "var(--color-text-primary)" }}>
              Meridian KPI Platform
            </span>
          </Link>
        </div>

        <div
          className="rounded-2xl p-8 shadow-lg"
          style={{ backgroundColor: "var(--color-bg-card)", border: "1px solid var(--color-border)" }}
        >
          {submitted ? (
            /* Success State */
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 border border-emerald-200 rounded-full flex items-center justify-center mb-5">
                <CheckCircle size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>Check your inbox</h2>
              <p className="text-sm leading-relaxed max-w-xs mb-2" style={{ color: "var(--color-text-muted)" }}>
                If an account exists for{" "}
                <span className="font-medium" style={{ color: "var(--color-text-primary)" }}>{email}</span>, we&apos;ve sent a password reset link.
              </p>
              <p className="text-xs mb-8" style={{ color: "var(--color-text-muted)" }}>
                Didn&apos;t receive it? Check your spam folder or wait a few minutes.
              </p>

              <div className="w-full space-y-3">
                <button
                  onClick={() => { setSubmitted(false); setEmail(""); }}
                  className="w-full inline-flex items-center justify-center gap-2 border rounded-xl py-2.5 text-sm font-medium transition-all hover:bg-blue-50"
                  style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
                >
                  <Mail size={15} />
                  Try a different email
                </button>
                <Link
                  href="/login"
                  className="w-full inline-flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors py-1"
                >
                  <ArrowLeft size={15} />
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            /* Form State */
            <>
              {/* Back link */}
              <Link
                href="/login"
                className="inline-flex items-center gap-1.5 text-sm transition-colors mb-6 hover:text-blue-600"
                style={{ color: "var(--color-text-muted)" }}
              >
                <ArrowLeft size={14} />
                Back to sign in
              </Link>

              <div className="mb-7">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: "var(--color-accent-light)", border: "1px solid var(--color-border)" }}
                >
                  <Mail size={22} className="text-blue-600" />
                </div>
                <h1 className="text-2xl font-bold mb-1.5" style={{ color: "var(--color-text-primary)" }}>Forgot your password?</h1>
                <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                  No problem. Enter your email and we&apos;ll send you a reset link straight away.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--color-text-muted)" }} />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                      placeholder="you@example.com"
                      className={`w-full rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none transition-colors border ${emailError ? "border-red-400 focus:border-red-500" : "focus:border-blue-500"}`}
                      style={{
                        backgroundColor: "var(--color-bg-input)",
                        borderColor: emailError ? undefined : "var(--color-border-input)",
                        color: "var(--color-text-primary)",
                      }}
                    />
                  </div>
                  {emailError && <p className="text-red-600 text-xs mt-1.5">{emailError}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed py-2.5 rounded-xl font-semibold text-sm text-white transition-all shadow-md shadow-blue-500/20"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Sending reset link…
                    </>
                  ) : (
                    <>
                      Send Reset Link
                      <Send size={15} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {!submitted && (
          <p className="text-center text-sm mt-5" style={{ color: "var(--color-text-muted)" }}>
            Remember your password?{" "}
            <Link href="/login" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
